import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import DocumentUpload from "../../shared/DocumentUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

const MAX_FILE_MB = 10;

const getValidationSchema = (isEdit: boolean) =>
  Yup.object({
    title: Yup.string().required("Title is required"),
    document_type: Yup.string()
      .oneOf(["file_upload", "external_link"])
      .required("Type is required"),
    link_url: Yup.mixed().when("document_type", {
      is: "external_link",
      then: () =>
        Yup.string().required("Link URL is required"),
      otherwise: () =>
        Yup.mixed()
          .nullable()
          .test(
            "fileRequired",
            "Please upload a file",
            (value: any) => Boolean(value),
          )
          .test(
            "fileSize",
            `File must be under ${MAX_FILE_MB}MB`,
            (value: any) => !(value instanceof File) || value.size <= MAX_FILE_MB * 1024 * 1024,
          ),
    }),
    sort_order: Yup.string().required("Sort Order is required"),
  });

export default function MandatoryDisclosureCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      icon: "",
      link_text: "",
      link_url: "",
      view_mode: "new_tab",
      sort_order: "",
      isActive: "Active",
      document_type: "file_upload",
    },
    validationSchema: getValidationSchema(isEdit),
    onSubmit: async (values) => {
      const isFileUpload = values.document_type === "file_upload";

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("icon", values.icon);
      formData.append("link_text", values.link_text);
      formData.append("view_mode", values.view_mode);
      formData.append("sort_order", values.sort_order);
      formData.append("status", values.isActive === "Active" ? "true" : "false");
      formData.append("document_type", values.document_type);

      if (isFileUpload) {
        if (values.link_url instanceof File) {
          formData.append("link_url", values.link_url);
        }
      } else {
        formData.append("link_url", values.link_url);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("mandatory-disclosures/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("mandatory-disclosures/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(
            isEdit
              ? "Disclosure updated successfully!"
              : "Disclosure created successfully!",
          );
          navigate("/admin/mandatory-disclosure");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`mandatory-disclosures/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            icon: item.icon || "",
            link_text: item.link_text || "",
            link_url: item.link_url || "",
            view_mode: item.view_mode || "new_tab",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
            document_type: item.document_type || "external_link",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDocumentTypeChange = (e: any) => {
    formik.setFieldValue("document_type", e.target.value);
    formik.setFieldValue("link_url", "", false);
    formik.setFieldTouched("link_url", false);
  };

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Disclosure" : "Create New Disclosure"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/mandatory-disclosure"),
              }}
              primaryButton={{
                label: isEdit ? "Update Disclosure" : "Create Disclosure",
                icon: (
                  <span className="material-symbols-outlined">save</span>
                ),
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="title"
                    label="Title"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="icon"
                    label="Icon"
                    type="text"
                    formik={formik}
                    placeholder="e.g. description"
                  />
                </Box>
              </Box>

              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="link_text"
                    label="Link Text"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="document_type"
                    label="Document Type"
                    formik={formik}
                    onChange={handleDocumentTypeChange}
                    options={[
                      { value: "external_link", label: "External Link" },
                      { value: "file_upload", label: "File Upload" },
                    ]}
                  />
                </Box>
              </Box>

              {formik.values.document_type === "external_link" && (
                <Box
                  className="form-grid form-grid--2"
                  style={{ marginTop: "16px" }}
                >
                  <Box className="form-field">
                    <TextField
                      name="link_url"
                      label="Link URL"
                      type="text"
                      formik={formik}
                      placeholder="https://..."
                    />
                  </Box>
                </Box>
              )}

              {formik.values.document_type === "file_upload" && (
                <Box
                  className="form-grid form-grid--2"
                  style={{ marginTop: "16px" }}
                >
                  <Box className="form-field">
                    <DocumentUpload name="link_url" formik={formik} label="PDF, DOC, DOCX, JPG, PNG" />
                  </Box>
                </Box>
              )}

              <Box
                className="form-grid form-grid--3"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <SelectField
                    name="view_mode"
                    label="Open Link In"
                    formik={formik}
                    options={[
                      { value: "new_tab", label: "New Tab" },
                      { value: "same_tab", label: "Same Tab" },
                    ]}
                    disabled={formik.values.document_type === "file_upload"}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}